import {
  PAIR_POSES,
  PANEL_ANGLES,
  PANEL_BACKDROPS,
  PANEL_BALLOONS,
  PANEL_BORDERS,
  PANEL_CAPTION_AT,
  PANEL_CONDITION_MARKS,
  PANEL_EFFECTS,
  PANEL_EFFECT_SIZES,
  PANEL_EXPRESSIONS,
  PANEL_FACINGS,
  PANEL_HEADWEAR,
  PANEL_JACKETS,
  PANEL_LOOKS,
  PANEL_MARK_PLACES,
  PANEL_MARKS,
  PANEL_ON,
  PANEL_POSES,
  PANEL_PRESETS,
  PANEL_PROPS,
  PANEL_SETS,
  PANEL_SHOTS,
  PANEL_SIDES,
  PANEL_SKIN_TONES,
  PANEL_SLOTS,
  PANEL_SPEED_DIRS,
  PANEL_SUBJECT_SHOTS,
  PANEL_WORN_MODELS,
  ZOMBIE_POSES,
} from '../story/panelIds';
import {
  checkClipPose,
  isClipPose,
  MAX_POSE_VARIANTS,
  poseVariantCount,
} from '../story/panelPoses';
import { validateCastLooks } from './cast';
import { checkTemplate, roleKnown } from '../story/template';
import type { CellDef, PanelRulesDef, PanelSpec } from '../story/types';
import { isBlank, num, type Err, type Ids } from './common';
import { idSet, sectionEntries } from './sections';
import type { Bundle } from './types';

/** `ContentValidator` panel limits (S1-CHR). */
export const MAX_CAPTION_CHARS = 120;
export const MAX_BALLOON_CHARS = 90;
export const MAX_PANEL_ACTORS = 6;
export const MAX_PANEL_BALLOONS = 3;
/** The built-in pronoun sets (`PronounSets.Builtin`): known even when the bundle has none. */
const BUILTIN_PRONOUNS = ['she', 'he', 'they'];

/** `PanelCellContext` (S1-CHR): what a cell may name. */
export type PanelCellContext = {
  castRoles: Set<string>;
  npcs: Set<string>;
  zombies: Set<string>;
  roomTemplates: Set<string>;
  strings: Record<string, string>;
  rules: PanelRulesDef;
  hasChoice: boolean;
};

const is = (list: readonly string[], v: string | null | undefined) => v != null && list.includes(v);
const known = (ctx: PanelCellContext, role: string | null | undefined) =>
  !!role && roleKnown(role, ctx.castRoles, ctx.npcs, ctx.zombies);
const isZombie = (role: string | null | undefined) => !!role && role.startsWith('zombie:');

/** shared-cs `PanelAdjustLimitsDef` defaults (ADR 0059 phase 2; Story Lab plan §4.1). */
export const ADJUST_LIMIT_DEFAULTS = {
  yaw: 60,
  pitchMin: -25,
  pitchMax: 35,
  dollyMin: 0.6,
  dollyMax: 1.6,
  fov: 12,
  pan: 0.3,
  nudge: 1.5,
  turn: 180,
};
/** `panelRules.adjustLimits` with each left-out member at its default. */
export function adjustLimits(r: PanelRulesDef | null | undefined): typeof ADJUST_LIMIT_DEFAULTS {
  const l = r?.adjustLimits ?? {};
  const out = { ...ADJUST_LIMIT_DEFAULTS };
  (Object.keys(out) as (keyof typeof out)[]).forEach((k) => {
    if (l[k] != null) out[k] = l[k]!;
  });
  return out;
}
const inRange = (v: number, lo: number, hi: number) => Number.isFinite(v) && v >= lo && v <= hi;
/** C# `0.##` (invariant). */
const fmt = (v: number) => String(Number(v.toFixed(2)));

/** `ContentValidator.Place`: a stage mark, `setpiece:<anchor>`, `role:`/`behind:`/`near:` a known role (near may add `.part`). */
function place(p: string | null | undefined, ctx: PanelCellContext): boolean {
  if (!p) return false;
  if (is(PANEL_MARK_PLACES, p)) return true;
  const colon = p.indexOf(':');
  if (colon <= 0) return false;
  const kind = p.slice(0, colon);
  const rest = p.slice(colon + 1);
  switch (kind) {
    case 'setpiece':
      return rest.length > 0;
    case 'role':
    case 'behind':
      return known(ctx, rest);
    case 'near': {
      const dot = rest.lastIndexOf('.');
      return known(ctx, rest) || (dot > 0 && known(ctx, rest.slice(0, dot)));
    }
    default:
      return false;
  }
}

const toward = (v: string | null | undefined, words: readonly string[], ctx: PanelCellContext) =>
  is(words, v) || (!!v && v.startsWith('role:') && known(ctx, v.slice(5)));

/** A problem with its dotted path under the cell's panel (`actors.0.pose`). */
export type PanelReport = (path: string, message: string) => void;

/**
 * `ContentValidator.PanelCell` (S1-CHR): one cell's panel spec with the server's messages. `ids` are the chain's cell ids so far,
 * `earlier` the cells before this one in the same list.
 */
export function panelCell(
  p: PanelSpec,
  ctx: PanelCellContext,
  where: string,
  ids: Set<string>,
  earlier: Set<string>,
  report: PanelReport,
) {
  const id = p.id ?? '';
  const w = `${where} cell '${id}'`;
  if (isBlank(id)) report('id', `${where} has a cell with no id.`);
  else if (ids.has(id)) report('id', `${where} has a duplicate cell id '${id}'.`);
  else ids.add(id);

  const layout = p.layout ?? {};
  const slot = layout.slot ?? 'auto';
  if (!is(PANEL_SLOTS, slot)) report('layout.slot', `${w} has unknown slot '${slot}'.`);
  if (!is(PANEL_BORDERS, layout.border ?? 'ink'))
    report('layout.border', `${w} has unknown border '${layout.border}'.`);
  if (layout.mask && !is(['scope', 'round'], layout.mask))
    report('layout.mask', `${w} has unknown mask '${layout.mask}'.`);
  const inset = slot === 'inset';
  if (inset !== !!layout.insetOf)
    report('layout.insetOf', `${w} needs slot 'inset' and insetOf together.`);
  else if (inset && !earlier.has(layout.insetOf ?? ''))
    report(
      'layout.insetOf',
      `${w} is an inset of '${layout.insetOf}', which isn't an earlier cell.`,
    );

  const shot = p.shot ?? {};
  const type = shot.type ?? 'medium';
  if (!is(PANEL_SHOTS, type)) report('shot.type', `${w} has unknown shot '${type}'.`);
  if (is(PANEL_SUBJECT_SHOTS, type) || shot.subject) {
    if (!known(ctx, shot.subject))
      report('shot.subject', `${w} frames unknown role '${shot.subject ?? ''}'.`);
  }
  if (type === 'ots' && !known(ctx, shot.from))
    report('shot.from', `${w} is over the shoulder of unknown role '${shot.from ?? ''}'.`);
  if (shot.on && !is(PANEL_ON, shot.on))
    report('shot.on', `${w} has unknown shot.on '${shot.on}'.`);
  if (!is(PANEL_ANGLES, shot.angle ?? 'eye'))
    report('shot.angle', `${w} has unknown angle '${shot.angle}'.`);
  if (!is(PANEL_SIDES, shot.side ?? 'auto'))
    report('shot.side', `${w} has unknown side '${shot.side}'.`);
  if (num(shot.dutch) < -15 || num(shot.dutch) > 15)
    report('shot.dutch', `${w} needs dutch in -15..15.`);
  if (shot.adjust) {
    // ADR 0059 phase 2 (card #302): shot.adjust inside panelRules.adjustLimits (left-out members are their defaults).
    const l = adjustLimits(ctx.rules),
      a = shot.adjust;
    const rng = (
      k: keyof typeof a,
      v: number | null | undefined,
      d: number,
      lo: number,
      hi: number,
    ) =>
      void (
        inRange(v ?? d, lo, hi) ||
        report(`shot.adjust.${k}`, `${w} needs shot adjust ${k} in ${fmt(lo)}..${fmt(hi)}.`)
      );
    rng('yaw', a.yaw, 0, -l.yaw, l.yaw);
    rng('pitch', a.pitch, 0, l.pitchMin, l.pitchMax);
    rng('dolly', a.dolly, 1, l.dollyMin, l.dollyMax);
    rng('fov', a.fov, 0, -l.fov, l.fov);
    rng('panX', a.panX, 0, -l.pan, l.pan);
    rng('panY', a.panY, 0, -l.pan, l.pan);
  }

  const actors = p.actors ?? [];
  const maxActors = Math.min(
    MAX_PANEL_ACTORS,
    num(ctx.rules.maxActors) > 0 ? num(ctx.rules.maxActors) : MAX_PANEL_ACTORS,
  );
  if (actors.length === 0) report('actors', `${w} has no actors.`);
  if (actors.length > maxActors)
    report('actors', `${w} has ${actors.length} actors (at most ${maxActors}).`);
  const present = new Set(actors.filter((a) => a?.role).map((a) => a!.role!));
  actors.forEach((a, i) => {
    const at = (f: string) => `actors.${i}.${f}`;
    if (a == null) {
      report(`actors.${i}`, `${w} has an empty actor.`);
      return;
    }
    const role = a.role ?? '';
    const aw = `${w} actor '${role}'`;
    if (!known(ctx, role)) {
      report(at('role'), `${w} has unknown role '${role}'.`);
      return;
    }
    const variant = a.poseVariant ?? 0;
    if (!a.pose) report(at('pose'), `${aw} needs a pose.`);
    else if (isClipPose(a.pose)) {
      // Card #303: a frame of any clip in the catalogue (clip:<id>@<t>[:mirror]); no variants.
      const why = checkClipPose(a.pose, isZombie(role));
      if (why) report(at('pose'), `${aw} ${why}.`);
      if (variant !== 0)
        report(
          at('poseVariant'),
          `${aw} has pose variant ${variant}, but a clip pose has no variants.`,
        );
    } else if (!is(PANEL_POSES, a.pose)) report(at('pose'), `${aw} has unknown pose '${a.pose}'.`);
    else if (isZombie(role) && !is(ZOMBIE_POSES, a.pose))
      report(
        at('pose'),
        `${aw} is a zombie and can't take pose '${a.pose}' (lunge, feed, grab or dead).`,
      );
    else if (variant < 0) report(at('poseVariant'), `${aw} needs pose variant 0 or more.`);
    else if (variant > 0) {
      const count = poseVariantCount(ctx.rules, a.pose, isZombie(role));
      if (variant >= count)
        report(
          at('poseVariant'),
          `${aw} has pose variant ${variant}, but pose '${a.pose}' has ${count} (panelRules.poses).`,
        );
    }
    if (a.expression && !is(PANEL_EXPRESSIONS, a.expression))
      report(at('expression'), `${aw} has unknown expression '${a.expression}'.`);
    // Card #304: amounts 0..1 (left out = 1) and an optional second shape.
    const amountOk = (v: number | null | undefined) => v == null || (v >= 0 && v <= 1);
    if (!amountOk(a.expressionAmount))
      report(at('expressionAmount'), `${aw} needs expression amount in 0..1.`);
    if (a.expression2) {
      if (!is(PANEL_EXPRESSIONS, a.expression2))
        report(at('expression2'), `${aw} has unknown second expression '${a.expression2}'.`);
      else if (a.expression2 === 'turned')
        report(
          at('expression2'),
          `${aw} has second expression 'turned' (only the main expression can turn a survivor).`,
        );
      else if (a.expression2 === (a.expression || 'neutral'))
        report(
          at('expression2'),
          `${aw} has the same second expression as its first ('${a.expression2}').`,
        );
      if (!amountOk(a.expression2Amount))
        report(at('expression2Amount'), `${aw} needs second expression amount in 0..1.`);
    }
    const mark = a.mark ?? 'center';
    if (!place(mark, ctx) || mark.startsWith('role:'))
      report(at('mark'), `${aw} has unknown mark '${mark}'.`);
    if (!toward(a.facing ?? 'camera', PANEL_FACINGS, ctx))
      report(at('facing'), `${aw} has unknown facing '${a.facing}'.`);
    if (!toward(a.lookAt ?? 'camera', PANEL_LOOKS, ctx))
      report(at('lookAt'), `${aw} has unknown lookAt '${a.lookAt}'.`);
    const hold = a.hold ?? 'weapon';
    if (hold.startsWith('prop:')) {
      if (!is(PANEL_PROPS, hold.slice(5)))
        report(at('hold'), `${aw} holds unknown prop '${hold.slice(5)}'.`);
    } else if (!is(['weapon', 'none'], hold))
      report(at('hold'), `${aw} has unknown hold '${hold}' (weapon, none or prop:<id>).`);
    if (a.pairWith) {
      if (!present.has(a.pairWith) || a.pairWith === role)
        report(
          at('pairWith'),
          `${aw} pairs with '${a.pairWith}', who isn't another actor in the cell.`,
        );
      if (!is(PAIR_POSES, a.pose))
        report(
          at('pairWith'),
          `${aw} pairs with another actor but pose '${a.pose ?? ''}' isn't a pair pose (grab, dragged or fallen).`,
        );
    }
    (a.marks ?? []).forEach((m, j) => {
      if (m == null || !is(PANEL_MARKS, m.id))
        report(at(`marks.${j}.id`), `${aw} has unknown mark id '${m?.id ?? ''}'.`);
    });
    if (a.nudge) {
      // ADR 0059 phase 2 (card #302): metres in the mark frame and degrees, inside panelRules.adjustLimits.
      const l = adjustLimits(ctx.rules),
        n = a.nudge;
      const rng = (k: 'dx' | 'dz' | 'turn', lim: number) =>
        void (
          inRange(n[k] ?? 0, -lim, lim) ||
          report(at(`nudge.${k}`), `${aw} needs nudge ${k} in ${fmt(-lim)}..${fmt(lim)}.`)
        );
      rng('dx', l.nudge);
      rng('dz', l.nudge);
      rng('turn', l.turn);
    }
  });

  const set = p.set ?? {};
  const kind = set.kind ?? 'room';
  if (!is(PANEL_SETS, kind)) report('set.kind', `${w} has unknown set '${kind}'.`);
  if (kind === 'kit_room' && !ctx.roomTemplates.has(set.template ?? ''))
    report(
      'set.template',
      `${w} has a kit_room with unknown room template '${set.template ?? ''}'.`,
    );
  if (kind !== 'kit_room' && set.template)
    report('set.template', `${w} has a room template but its set isn't kit_room.`);

  const light = p.lighting ?? {};
  const preset = light.preset ?? 'day';
  if (!is(PANEL_PRESETS, preset))
    report('lighting.preset', `${w} has unknown lighting '${preset}'.`);
  if (light.from && !place(light.from, ctx))
    report('lighting.from', `${w} has lighting from unknown '${light.from}'.`);
  if (light.backdrop) {
    if (preset !== 'silhouette')
      report('lighting.backdrop', `${w} has a backdrop but its lighting isn't silhouette.`);
    else if (!is(PANEL_BACKDROPS, light.backdrop))
      report('lighting.backdrop', `${w} has unknown backdrop '${light.backdrop}' (yellow or red).`);
  }

  (p.effects ?? []).forEach((e, i) => {
    const at = (f: string) => `effects.${i}.${f}`;
    if (e == null) {
      report(`effects.${i}`, `${w} has an empty effect.`);
      return;
    }
    const ew = `${w} effect '${e.type ?? ''}'`;
    if (!is(PANEL_EFFECTS, e.type)) {
      report(at('type'), `${w} has unknown effect '${e.type ?? ''}'.`);
      return;
    }
    if (e.size && !is(PANEL_EFFECT_SIZES, e.size))
      report(at('size'), `${ew} has unknown size '${e.size}' (s, m or l).`);
    if (e.at && !place(e.at, ctx)) report(at('at'), `${ew} is at unknown '${e.at}'.`);
    if (e.type === 'sfx' && (!e.word || !(e.word in ctx.strings)))
      report(at('word'), `${ew} needs a word from the strings (missing '${e.word ?? ''}').`);
    if (e.type === 'speed_lines') {
      if (!is(PANEL_SPEED_DIRS, e.dir))
        report(at('dir'), `${ew} has unknown dir '${e.dir ?? ''}'.`);
      if (e.around && !known(ctx, e.around))
        report(at('around'), `${ew} is around unknown role '${e.around}'.`);
    }
    if (e.type === 'vignette' && (num(e.amount) < 0 || num(e.amount) > 1))
      report(at('amount'), `${ew} needs amount in 0..1.`);
  });

  if (p.caption) {
    if (!is(PANEL_CAPTION_AT, p.caption.at ?? 'top'))
      report('caption.at', `${w} has a caption at unknown '${p.caption.at}' (top or bottom).`);
    panelString(p.caption.text, MAX_CAPTION_CHARS, 'caption', ctx, w, 'caption.text', report);
  }
  const lines = p.dialogue ?? [];
  const maxBalloons = Math.min(
    MAX_PANEL_BALLOONS,
    num(ctx.rules.maxBalloonsPerCell) > 0 ? num(ctx.rules.maxBalloonsPerCell) : MAX_PANEL_BALLOONS,
  );
  if (lines.length > maxBalloons)
    report('dialogue', `${w} has ${lines.length} balloons (at most ${maxBalloons}).`);
  lines.forEach((l, i) => {
    if (l == null) {
      report(`dialogue.${i}`, `${w} has an empty balloon.`);
      return;
    }
    if (!known(ctx, l.speaker))
      report(`dialogue.${i}.speaker`, `${w} has a balloon from unknown role '${l.speaker ?? ''}'.`);
    if (!is(PANEL_BALLOONS, l.kind ?? 'speech'))
      report(`dialogue.${i}.kind`, `${w} has unknown balloon kind '${l.kind}'.`);
    panelString(l.text, MAX_BALLOON_CHARS, 'balloon', ctx, w, `dialogue.${i}.text`, report);
  });
  if (p.decision && !ctx.hasChoice)
    report('decision', `${w} is a decision cell, but its beat has no choice.`);
}

function panelString(
  key: string | null | undefined,
  max: number,
  what: string,
  ctx: PanelCellContext,
  w: string,
  path: string,
  report: PanelReport,
) {
  if (!key || !(key in ctx.strings)) {
    report(path, `${w} has a ${what} with missing string '${key ?? ''}'.`);
    return;
  }
  const text = ctx.strings[key] ?? '';
  if (text.length > max) report(path, `${w} ${what} '${key}' is over ${max} characters.`);
  for (const m of checkTemplate(text, (r) => known(ctx, r), `${w} ${what} '${key}'`))
    report(path, m);
}

/** `ContentValidator.PanelCells` over one list (a beat's or an outcome's cells). */
export function panelCells(
  cells: (CellDef | null)[] | null | undefined,
  ctx: PanelCellContext,
  where: string,
  ids: Set<string>,
  base: string,
  report: PanelReport,
) {
  const listed = new Set<string>();
  (cells ?? []).forEach((cell, n) => {
    if (cell?.panel == null) {
      report(`${base}.${n}`, `${where} has an empty cell.`);
      return;
    }
    panelCell(cell.panel, ctx, where, ids, listed, (p, m) =>
      report(`${base}.${n}.panel${p ? `.${p}` : ''}`, m),
    );
    if (cell.panel.id) listed.add(cell.panel.id);
  });
}

/**
 * The panel sections: a port of shared-cs `ContentValidator.Panels.cs` (S1-CHR): `panelRules`, `pronounSets`, `npcs`, armor
 * `wornModel`, condition `panelMark`, straggler survivors' pronouns, then every beat chain's cells (on section `beatChains`).
 */
export function validatePanels(b: Bundle, err: Err, ids: Ids) {
  if (b.panelRules === null) err('panelRules', undefined, '', "Bundle is missing 'panelRules'.");
  else panelRules(b.panelRules ?? {}, err);

  // Pronoun sets.
  const sets = sectionEntries('pronounSets', b.pronounSets, err);
  const pronounIds = ids('pronounSets', b.pronounSets ?? [], 'pronoun set');
  for (const [s, i] of sets) {
    if (isBlank(s.id)) continue;
    if (s.id !== s.id!.trim().toLowerCase())
      err('pronounSets', i, 'id', `Pronoun set '${s.id}' needs a lower-case id.`);
    if ([s.they, s.them, s.their, s.theirs, s.themself].some(isBlank))
      err(
        'pronounSets',
        i,
        'they',
        `Pronoun set '${s.id}' needs they, them, their, theirs and themself.`,
      );
  }
  if (sets.length > 0 && !pronounIds.has('they'))
    err('pronounSets', undefined, '', "Pronoun sets need 'they' (the default).");
  const pronounKnown = (id: string | null | undefined) =>
    !!id && (pronounIds.has(id) || BUILTIN_PRONOUNS.includes(id));

  // NPCs.
  const npcs = sectionEntries('npcs', b.npcs, err);
  ids('npcs', b.npcs ?? [], 'npc');
  for (const [n, i] of npcs) {
    const w = `NPC '${n.id ?? ''}'`;
    if (isBlank(n.name)) err('npcs', i, 'name', `${w} needs a name.`);
    if (!pronounKnown(n.pronouns ?? 'they'))
      err('npcs', i, 'pronouns', `${w} has unknown pronouns '${n.pronouns ?? ''}'.`);
    (n.wear ?? []).forEach((wear, j) => {
      const ok =
        is(PANEL_JACKETS, wear) ||
        is(PANEL_HEADWEAR, wear) ||
        is(PANEL_SKIN_TONES, wear) ||
        wear === 'turned' ||
        is(PANEL_WORN_MODELS, wear) ||
        is(PANEL_MARKS, wear);
      if (!ok) err('npcs', i, `wear.${j}`, `${w} wears unknown look id '${wear}'.`);
    });
  }
  validateCastLooks(b, err); // POP T31: appearance = cast:<key> (shared-cs ContentValidator.Cast)

  // Armor worn models and condition marks (fields S1-CHR added; read loosely until the client is regenerated).
  (b.items ?? []).forEach((item, i) => {
    const worn = (
      (item?.armor as { wornModel?: string } | null | undefined)?.wornModel ?? ''
    ).trim();
    if (!worn) return;
    for (const part of worn.split('+'))
      if (!is(PANEL_WORN_MODELS, part))
        err(
          'items',
          i,
          'armor.wornModel',
          `Armor '${item!.id}' has unknown wornModel '${part}' (vest, padded, arm_wraps or helmet).`,
        );
  });
  (b.conditions?.types ?? []).forEach((c, j) => {
    const mark = (c as { panelMark?: string } | null)?.panelMark ?? '';
    if (mark && !is(PANEL_CONDITION_MARKS, mark))
      err(
        'conditions',
        undefined,
        `types.${j}.panelMark`,
        `Condition '${c!.id}' has unknown panelMark '${mark}' (head_bandage, bite_wrap, sweat or sling).`,
      );
  });
  // Every chain's cells.
  const npcIds = idSet(b.npcs);
  const zombies = idSet(b.zombies);
  const rooms = idSet(b.roomTemplates);
  (b.beatChains ?? []).forEach((chain, i) => {
    if (chain == null) return;
    const ctx: PanelCellContext = {
      castRoles: new Set((chain.cast ?? []).map((r) => r?.role ?? '').filter(Boolean)),
      npcs: npcIds,
      zombies,
      roomTemplates: rooms,
      strings: chain.strings ?? {},
      rules: b.panelRules ?? {},
      hasChoice: false,
    };
    const cellIds = new Set<string>();
    const report = (path: string, m: string) => err('beatChains', i, path, m);
    for (const [id, beat] of Object.entries(chain.beats ?? {})) {
      ctx.hasChoice = beat?.choice != null;
      panelCells(
        beat?.cells,
        ctx,
        `Chain '${chain.id ?? ''}' beat '${id}'`,
        cellIds,
        `beats.${id}.cells`,
        report,
      );
    }
    ctx.hasChoice = false;
    for (const [id, o] of Object.entries(chain.outcomes ?? {}))
      panelCells(
        o?.cells,
        ctx,
        `Chain '${chain.id ?? ''}' outcome '${id}'`,
        cellIds,
        `outcomes.${id}.cells`,
        report,
      );
  });
}

function panelRules(r: PanelRulesDef, err: Err) {
  const where = 'Panel rules';
  const fail = (path: string, m: string) => err('panelRules', undefined, path, m);
  const v = (k: keyof PanelRulesDef, d: number) => (r[k] === undefined ? d : num(r[k] as number));
  if (v('maxActors', 6) < 1 || v('maxActors', 6) > MAX_PANEL_ACTORS)
    fail('maxActors', `${where} need maxActors in 1..${MAX_PANEL_ACTORS}.`);
  if (v('maxBalloonsPerCell', 3) < 1 || v('maxBalloonsPerCell', 3) > MAX_PANEL_BALLOONS)
    fail('maxBalloonsPerCell', `${where} need maxBalloonsPerCell in 1..${MAX_PANEL_BALLOONS}.`);
  if (v('gutterFrac', 0.025) < 0 || v('gutterFrac', 0.025) > 0.1)
    fail('gutterFrac', `${where} need gutterFrac in 0..0.1.`);
  if (v('swipeFrac', 0.18) < 0.05 || v('swipeFrac', 0.18) > 0.5)
    fail('swipeFrac', `${where} need swipeFrac in 0.05..0.5.`);
  if (v('lowTierFirstCellMs', 150) < 50 || v('lowTierFirstCellMs', 150) > 1000)
    fail('lowTierFirstCellMs', `${where} need lowTierFirstCellMs in 50..1000.`);
  if (v('cacheCells', 8) < 1 || v('cacheCells', 8) > 16)
    fail('cacheCells', `${where} need cacheCells in 1..16.`);
  const tiers = new Set<string>();
  (r.tiers ?? []).forEach((t, i) => {
    if (t == null || !['high', 'mid', 'low'].includes(t.id ?? '')) {
      fail(`tiers.${i}.id`, `${where} have unknown tier '${t?.id ?? ''}' (high, mid or low).`);
      return;
    }
    if (tiers.has(t.id!)) fail(`tiers.${i}.id`, `${where} list tier '${t.id}' twice.`);
    tiers.add(t.id!);
    if (num(t.width) < 64 || num(t.width) > 2048 || num(t.height) < 64 || num(t.height) > 2048)
      fail(`tiers.${i}.width`, `${where} tier '${t.id}' needs width and height in 64..2048.`);
    if (![1, 2, 4, 8].includes(t.samples ?? 1))
      fail(`tiers.${i}.samples`, `${where} tier '${t.id}' needs samples 1, 2, 4 or 8.`);
  });
  // Card #303: panelRules.poses (each pose once; variants are clip poses; zombieVariants only zombie clips on zombie poses).
  const posesSeen = new Set<string>();
  const variantList = (
    list: string[] | null | undefined,
    zombie: boolean,
    pw: string,
    path: string,
    field: string,
  ) => {
    if (!list) return;
    if (list.length > MAX_POSE_VARIANTS)
      fail(path, `${pw} lists ${list.length} ${field} (at most ${MAX_POSE_VARIANTS}).`);
    list.forEach((v, j) => {
      const val = v ?? '';
      if (!isClipPose(val)) {
        fail(
          `${path}.${j}`,
          `${pw} ${field}[${j}] '${val}' isn't a clip pose (clip:<clipId>@<0..1>).`,
        );
        return;
      }
      const why = checkClipPose(val, zombie);
      if (why) fail(`${path}.${j}`, `${pw} ${field}[${j}] ${why}.`);
    });
  };
  (r.poses ?? []).forEach((row, i) => {
    if (row == null || !is(PANEL_POSES, row.pose)) {
      fail(`poses.${i}.pose`, `${where} have a pose row for unknown pose '${row?.pose ?? ''}'.`);
      return;
    }
    const pw = `${where} pose '${row.pose}'`;
    if (posesSeen.has(row.pose!))
      fail(`poses.${i}.pose`, `${where} list pose '${row.pose}' twice.`);
    posesSeen.add(row.pose!);
    variantList(row.variants, false, pw, `poses.${i}.variants`, 'variants');
    if (row.zombieVariants && row.zombieVariants.length > 0) {
      if (!is(ZOMBIE_POSES, row.pose))
        fail(`poses.${i}.zombieVariants`, `${pw} has zombieVariants, but zombies can't take it.`);
      else variantList(row.zombieVariants, true, pw, `poses.${i}.zombieVariants`, 'zombieVariants');
    }
  });
  const shots = new Set<string>();
  (r.shots ?? []).forEach((s, i) => {
    if (s == null || !is(PANEL_SHOTS, s.shot)) {
      fail(`shots.${i}.shot`, `${where} have unknown shot '${s?.shot ?? ''}'.`);
      return;
    }
    if (shots.has(s.shot!)) fail(`shots.${i}.shot`, `${where} list shot '${s.shot}' twice.`);
    shots.add(s.shot!);
    const fill = s.fill ?? 1;
    const fov = s.fov ?? 30;
    if (!(fill > 0) || fill > 1)
      fail(`shots.${i}.fill`, `${where} shot '${s.shot}' needs fill in (0..1].`);
    if (fov < 10 || fov > 60)
      fail(`shots.${i}.fov`, `${where} shot '${s.shot}' needs fov in 10..60.`);
  });
  if (r.adjustLimits) {
    // ADR 0059 phase 2: each limit inside fixed outer bounds.
    const l = adjustLimits(r);
    const bound = (k: keyof typeof l, lo: number, hi: number) =>
      void (
        inRange(l[k], lo, hi) ||
        fail(`adjustLimits.${k}`, `${where} adjustLimits need ${k} in ${fmt(lo)}..${fmt(hi)}.`)
      );
    bound('yaw', 0, 90);
    bound('pitchMin', -60, 0);
    bound('pitchMax', 0, 60);
    bound('dollyMin', 0.2, 1);
    bound('dollyMax', 1, 3);
    bound('fov', 0, 25);
    bound('pan', 0, 0.5);
    bound('nudge', 0, 3);
    bound('turn', 0, 180);
  }
}
